import { useState, useEffect } from 'react'
import { LoadingSpinner } from './LoadingSpinner'

interface HealthCheck {
  status: 'healthy' | 'unhealthy' | 'degraded'
  checks: {
    database: { status: 'pass' | 'fail'; message: string; timing?: number }
    secrets: { status: 'pass' | 'fail'; message: string; missing?: string[] }
    storage: { status: 'pass' | 'fail'; message: string }
    cache: { status: 'pass' | 'fail'; message: string }
  }
  timestamp: string
  environment: string
}

interface SystemStatusProps {
  onReady: () => void
  onError: (error: string) => void
}

export const SystemStatus: React.FC<SystemStatusProps> = ({ onReady, onError }) => {
  const [health, setHealth] = useState<HealthCheck | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787/api'
        const response = await fetch(`${API_BASE_URL}/health`)
        
        if (response.ok) {
          const healthData = await response.json()
          setHealth(healthData)
          
          if (healthData.status === 'healthy') {
            onReady()
          } else {
            const issues = Object.entries(healthData.checks)
              .filter(([, check]) => check.status === 'fail')
              .map(([name]) => name)
            onError(`System issues detected: ${issues.join(', ')}`)
          }
        } else {
          const errorData = await response.text()
          onError(`Health check failed (${response.status}): ${errorData}`)
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        setError(errorMessage)
        onError(`Cannot connect to backend: ${errorMessage}`)
      } finally {
        setLoading(false)
      }
    }

    checkHealth()
  }, [onReady, onError])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-600">กำลังตรวจสอบระบบ...</p>
          <p className="mt-2 text-sm text-gray-500">โปรดรอสักครู่</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h1 className="text-xl font-bold text-red-800 mb-2">ระบบไม่พร้อมใช้งาน</h1>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700"
            >
              ลองใหม่อีกครั้ง
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (health && health.status !== 'healthy') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-yellow-800 mb-4">สถานะระบบ</h2>
            <div className="space-y-2 text-sm">
              {Object.entries(health.checks).map(([name, check]) => (
                <div key={name} className="flex items-center justify-between">
                  <span className="capitalize">{name}:</span>
                  <span className={`font-medium ${check.status === 'pass' ? 'text-green-600' : 'text-red-600'}`}>
                    {check.status === 'pass' ? '✅' : '❌'} {check.message}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-4">Environment: {health.environment}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-yellow-700 w-full"
            >
              รีเฟรช
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Health check passed, render nothing (parent will handle)
  return null
}